import moment from 'moment';

// Helpers puros de la edición de registros de uso (024-services-record-edit).

// Texto y color de cada tipo de uso. Lo comparten Registrar Servicio y el 360 del usuario
// (035-coverage-usage-origin) para que el chip sea el mismo en ambas pantallas.
export const OPERATION_TYPES = {
  refund: { label: 'Reembolso', color: '#1CA762' },
  operational: { label: 'Operativo', color: '#5348CC' },
  upsell: { label: 'Upsell', color: '#F5A623' },
  telemedicine: { label: 'Telemedicina', color: '#0099CC' },
  petmed: { label: 'Petmed', color: '#E91E8C' }
};

const PET_PREFIX = /\[PET:([^\]]+)\]\s?/;

// Campos del registro que el PATCH admite (contracts/services-record-update.md).
const EDITABLE_FIELDS = [
  'servicePlanId',
  'isolatedServiceId',
  'userSpends',
  'refundedAmount',
  'refundable',
  'description',
  'operationType',
  'usedAt'
];
// Un stub de reembolso solo admite estos (FR-018).
const REFUND_FIELDS = ['usedAt', 'description', 'operationType'];
// Con atención (crear o rehacer) el backend los deriva de ella (FR-015).
const DERIVED_ON_PETMED_CONVERSION = [
  'servicePlanId',
  'isolatedServiceId',
  'userSpends',
  'refundedAmount',
  'refundable'
];
const AMOUNT_FIELDS = ['userSpends', 'refundedAmount'];

// Solo es 'refund' el stub SIN membresía. Los registros de reembolso con membresía (la
// mayoría en la base) se editan como cualquier registro con membresía.
export function recordVariant(record) {
  if (!record) return null;
  if (record.refundRequestId && !record.membershipId) return 'refund';
  if (!record.membershipId && !record.userId) return 'external';
  if (!record.membershipId) return 'unregistered';
  if (record.attendanceId) return 'petmed';
  return 'membership';
}

// Al crear se guarda como `${fullName} | ${desc}` (ServiceRecordMaintainer.create).
export function parseExternalDescription(description) {
  if (!description) return { fullName: null, description: null };
  const separator = description.indexOf('|');
  if (separator === -1) return { fullName: description.trim() || null, description: null };
  return {
    fullName: description.slice(0, separator).trim() || null,
    description: description.slice(separator + 1).trim() || null
  };
}

export function composeExternalDescription(fullName, description) {
  const realDescription = description ? ` | ${description}` : '';
  return `${fullName} ${realDescription}`;
}

export function parseUnregisteredDescription(description) {
  if (!description) return { petName: null, description: null };
  const match = description.match(PET_PREFIX);
  return {
    petName: match ? match[1] : null,
    description: description.replace(PET_PREFIX, '').trim() || null
  };
}

export function composeUnregisteredDescription(petName, description) {
  if (!petName) return description;
  return `[PET:${petName}] ${description ? ' ' + description : ''}`;
}

// usedAt es timestamptz: lo creado desde el admin queda a 00:00Z (se lee en UTC), lo que
// viene de PetMed o de la API pública trae hora real (se lee en el día local) — research R7.
export function recordDateForInput(usedAt) {
  if (!usedAt) return null;
  const utc = moment.utc(usedAt);
  const isMidnightUtc =
    utc.hours() === 0 && utc.minutes() === 0 && utc.seconds() === 0 && utc.milliseconds() === 0;
  return isMidnightUtc ? utc.format('YYYY-MM-DD') : moment(usedAt).format('YYYY-MM-DD');
}

function toAmount(value) {
  if (value === null || value === undefined || value === '') return null;
  return parseFloat(value);
}

function normalize(field, value) {
  if (AMOUNT_FIELDS.includes(field)) return toAmount(value);
  return value === undefined ? null : value;
}

// El body de la atención es el de la creación PetMed, sin el dato de UI isInGracePeriod.
function attendanceBody(attendance) {
  // eslint-disable-next-line no-unused-vars
  const { isInGracePeriod, ...body } = attendance;
  return body;
}

// Solo lo que cambió: el PATCH es parcial y la auditoría registra exactamente esos campos.
// `attendance` la pasa el componente cuando el registro queda PetMed y hay que crear o rehacer
// su atención; en ese caso servicio y montos los deriva el backend de la atención.
// membershipId solo viaja con la atención de un registro que no tenía membresía
// (stub de reembolso o usuario externo).
export function buildRecordPatch(original, current, variant, { attendance = null, membershipId = null } = {}) {
  let fields = variant === 'refund' ? REFUND_FIELDS : EDITABLE_FIELDS;
  if (attendance) fields = fields.filter((f) => !DERIVED_ON_PETMED_CONVERSION.includes(f));

  const patch = {};
  fields.forEach((field) => {
    const before = normalize(field, original[field]);
    const after = normalize(field, current[field]);
    if (before !== after) patch[field] = after;
  });

  // Cambiar de servicio de plan a aislado (o al revés) exige enviar ambos ids.
  if ('servicePlanId' in patch || 'isolatedServiceId' in patch) {
    patch.servicePlanId = normalize('servicePlanId', current.servicePlanId);
    patch.isolatedServiceId = normalize('isolatedServiceId', current.isolatedServiceId);
  }

  if (attendance && membershipId) patch.membershipId = membershipId;
  if (attendance) patch.attendance = attendanceBody(attendance);
  return patch;
}
